//
// ============================================================================
// IMPORTS
// ============================================================================
//

import type { Processor, Test, TestMapping } from "@skyware-group/iris-api-model";

import { unwrapPaged } from "../../services/api/domains/_shared/pagination";
import type {
  TestsByInstrumentParams,
  TestsFilterParams,
  TestCreateRequest,
  TestUpdateRequest,
  TestMappingCreateRequest,
  TestMappingUpdateRequest,
  ProcessorCreateRequest,
  ProcessorUpdateRequest,
} from "../../services/api/domains/tests/tests.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Tests Management Composable
 *
 * Manages laboratory test definitions and their mappings/processors.
 * All endpoints are nested under /instruments/{instrumentId}/tests/...
 *
 * @param key - Unique key for state isolation (default: "tests")
 */
export const useTests = (key = "tests") => {
  const { withAbort, abortAll } = useAbortableMultiFetch();
  const { handleError } = useErrorBoundary();

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  const loading = useState<boolean>(`${key}-loading`, () => false);
  const error = useState<string | null>(`${key}-error`, () => null);
  const tests = useState<Test[]>(`${key}-data`, () => []);
  const testsByInstrument = useState<Record<number, Test[]>>(`${key}-by-instrument`, () => ({}));

  // --------------------------------------------------------------------------
  // Computed
  // --------------------------------------------------------------------------

  const testsCount = computed(() => tests.value.length);
  const enabledTests = computed(() => tests.value.filter((t) => !t.isDisabled));
  const disabledTests = computed(() => tests.value.filter((t) => t.isDisabled));

  const testsGroupedByInstrument = computed(() => {
    const grouped: Record<number, Test[]> = {};
    tests.value.forEach((test) => {
      if (!grouped[test.instrumentId]) {
        grouped[test.instrumentId] = [];
      }
      grouped[test.instrumentId]!.push(test);
    });
    return grouped;
  });

  // --------------------------------------------------------------------------
  // Methods — Fetch
  // --------------------------------------------------------------------------

  /**
   * Fetch paginated tests for an instrument.
   * @param instrumentId - Instrument ID.
   * @param params - Optional pagination / filter params.
   */
  const fetchTestsByInstrument = async (
    instrumentId: number,
    params?: TestsByInstrumentParams,
  ): Promise<Test[] | null> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await withAbort<Test[]>(async (signal) => {
        const cl = await useProdClient();
        // Fetch-all: this feeds the panel-test picker, the instrument LIS-mapping
        // overview, and delete-dependency checks — all need the complete set, not a
        // single page. (The Tests tab uses `fetchTestsPage` for server pagination.)
        return collectAllPages(
          (page, pageSize, sig) => cl.tests.fetchTestsByInstrument(instrumentId, { ...params, page, pageSize }, sig),
          { signal },
        );
      });
      if (response) testsByInstrument.value[instrumentId] = response;
      return response;
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "fetchTestsByInstrument",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message ?? "Failed to fetch tests";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch one server page of tests for an instrument and return the page items
   * plus the backend total (for `v-data-table-server`). Unlike the other fetch
   * methods this **propagates** errors/aborts so the server-pagination engine can
   * classify them and avoid caching a failed page. Side-effect: caches the page
   * items under `testsByInstrument` for editor/duplicate-check consumers.
   */
  const fetchTestsPage = async (
    instrumentId: number,
    params?: TestsByInstrumentParams,
  ): Promise<{ items: Test[]; totalItems: number } | null> => {
    // Returns null when aborted (via withAbort); throws on real failures.
    return withAbort<{ items: Test[]; totalItems: number }>(async (signal) => {
      const cl = await useProdClient();
      // "All" (pageSize <= 0): walk every backend page (each ≤ max 100) and return
      // the complete set as one logical page — `-1` never reaches the backend.
      if (params?.pageSize !== undefined && params.pageSize <= 0) {
        const items = await collectAllPages(
          (page, pageSize, sig) => cl.tests.fetchTestsByInstrument(instrumentId, { ...params, page, pageSize }, sig),
          { signal },
        );
        testsByInstrument.value[instrumentId] = items;
        return { items, totalItems: items.length };
      }
      const paged = unwrapPaged<Test>(await cl.tests.fetchTestsByInstrument(instrumentId, params, signal));
      testsByInstrument.value[instrumentId] = paged.items;
      return { items: paged.items, totalItems: paged.page.totalItems };
    });
  };

  /**
   * Fetch the complete filtered set of tests for an instrument (walks all pages).
   * Used for lookups (e.g. duplicate-test checks), so it must return everything.
   */
  const fetchTestsFilter = async (instrumentId: number, params?: TestsFilterParams): Promise<Test[] | null> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await withAbort<Test[]>(async (signal) => {
        const cl = await useProdClient();
        return collectAllPages(
          (page, pageSize, sig) => cl.tests.fetchTestsFilter(instrumentId, { ...params, page, pageSize }, sig),
          {
            signal,
          },
        );
      });
      return response;
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "fetchTestsFilter",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch a single test by ID.
   * @param instrumentId - Instrument ID.
   * @param id - Test ID.
   */
  const fetchTestById = async (instrumentId: number, id: number): Promise<Test | null> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await withAbort<Test>(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.fetchTestById(instrumentId, id, signal);
      });
      return response;
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "fetchTestById",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message ?? `Failed to fetch test ${id}`;
      return null;
    } finally {
      loading.value = false;
    }
  };

  // --------------------------------------------------------------------------
  // Methods — CRUD
  // --------------------------------------------------------------------------

  /**
   * Create a new test. instrumentId is read from data.instrumentId.
   */
  const createTest = async (data: TestCreateRequest): Promise<Test | null> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await withAbort<Test>(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.createTest(data, signal);
      });
      if (!response) return null;
      tests.value.push(response);
      if (testsByInstrument.value[response.instrumentId]) {
        testsByInstrument.value[response.instrumentId]!.push(response);
      }
      return response;
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "createTest",
        fallbackKey: "components.common.errors.createFailed",
        showToast: true,
      });
      if (result.type === "abort") return null;
      error.value = result.message ?? "Failed to create test";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Update an existing test. instrumentId is read from data.instrumentId.
   */
  const updateTest = async (id: number, data: TestUpdateRequest): Promise<Test | null> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await withAbort<Test>(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.updateTest(id, data, signal);
      });
      if (!response) return null;
      const idx = tests.value.findIndex((t) => t.id === id);
      if (idx !== -1) tests.value.splice(idx, 1, response);
      const instrTests = testsByInstrument.value[response.instrumentId];
      if (instrTests) {
        const instrIdx = instrTests.findIndex((t) => t.id === id);
        if (instrIdx !== -1) instrTests.splice(instrIdx, 1, response);
      }
      return response;
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "updateTest",
        fallbackKey: "components.common.errors.updateFailed",
        showToast: true,
      });
      if (result.type === "abort") return null;
      error.value = result.message ?? `Failed to update test ${id}`;
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Delete a test.
   * @param instrumentId - Instrument ID (needed for the URL path).
   * @param id - Test ID.
   */
  const deleteTest = async (instrumentId: number, id: number): Promise<boolean> => {
    loading.value = true;
    error.value = null;
    try {
      await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.deleteTest(instrumentId, id, signal);
      });
      const idx = tests.value.findIndex((t) => t.id === id);
      if (idx !== -1) tests.value.splice(idx, 1);
      if (testsByInstrument.value[instrumentId]) {
        const instrIdx = testsByInstrument.value[instrumentId]!.findIndex((t) => t.id === id);
        if (instrIdx !== -1) testsByInstrument.value[instrumentId]!.splice(instrIdx, 1);
      }
      return true;
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "deleteTest",
        fallbackKey: "components.common.errors.deleteFailed",
      });
      if (result.type === "abort") return false;
      error.value = result.message ?? `Failed to delete test ${id}`;
      return false;
    } finally {
      loading.value = false;
    }
  };

  const clearAll = (): void => {
    tests.value = [];
    testsByInstrument.value = {};
  };

  // --------------------------------------------------------------------------
  // Methods — Mappings
  // --------------------------------------------------------------------------

  /**
   * Fetch all mappings for a test.
   */
  const fetchMappings = async (instrumentId: number, testId: number): Promise<TestMapping[] | null> => {
    try {
      return await withAbort<TestMapping[]>(async (signal) => {
        const cl = await useProdClient();
        return collectAllPages(
          (page, pageSize, sig) => cl.tests.fetchMappings(instrumentId, testId, { page, pageSize }, sig),
          { signal },
        );
      });
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "fetchMappings",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      return null;
    }
  };

  /**
   * Fetch one server page of mappings plus the backend total (for
   * `v-data-table-server`). Propagates errors/aborts so the server-pagination
   * engine can classify them; returns null only when aborted. `pageSize <= 0`
   * (the "All" sentinel) walks every page via `collectAllPages` — `-1` is never
   * forwarded to the backend.
   */
  const fetchMappingsPage = async (
    instrumentId: number,
    testId: number,
    params?: { page?: number; pageSize?: number },
  ): Promise<{ items: TestMapping[]; totalItems: number } | null> => {
    return withAbort<{ items: TestMapping[]; totalItems: number }>(async (signal) => {
      const cl = await useProdClient();
      if (params?.pageSize !== undefined && params.pageSize <= 0) {
        const items = await collectAllPages(
          (page, pageSize, sig) => cl.tests.fetchMappings(instrumentId, testId, { page, pageSize }, sig),
          { signal },
        );
        return { items, totalItems: items.length };
      }
      const paged = unwrapPaged<TestMapping>(await cl.tests.fetchMappings(instrumentId, testId, params, signal));
      return { items: paged.items, totalItems: paged.page.totalItems };
    });
  };

  /**
   * Create a new mapping for a test.
   */
  const createMapping = async (
    instrumentId: number,
    testId: number,
    data: TestMappingCreateRequest,
  ): Promise<TestMapping | null> => {
    try {
      return await withAbort<TestMapping>(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.createMapping(instrumentId, testId, data, signal);
      });
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "createMapping",
        fallbackKey: "components.common.errors.createFailed",
      });
      if (result.type === "abort") return null;
      return null;
    }
  };

  /**
   * Update an existing mapping by its natural key (mappingKey).
   */
  const updateMapping = async (
    instrumentId: number,
    testId: number,
    mappingKey: string,
    data: TestMappingUpdateRequest,
  ): Promise<TestMapping | null> => {
    try {
      return await withAbort<TestMapping>(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.updateMapping(instrumentId, testId, mappingKey, data, signal);
      });
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "updateMapping",
        fallbackKey: "components.common.errors.updateFailed",
      });
      if (result.type === "abort") return null;
      return null;
    }
  };

  /**
   * Delete a mapping by its natural key (mappingKey).
   */
  const deleteMapping = async (instrumentId: number, testId: number, mappingKey: string): Promise<boolean> => {
    try {
      await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.deleteMapping(instrumentId, testId, mappingKey, signal);
      });
      return true;
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "deleteMapping",
        fallbackKey: "components.common.errors.deleteFailed",
      });
      if (result.type === "abort") return false;
      return false;
    }
  };

  // --------------------------------------------------------------------------
  // Methods — Processors
  // --------------------------------------------------------------------------

  /**
   * Fetch all processors for a test.
   */
  const fetchProcessors = async (instrumentId: number, testId: number): Promise<Processor[] | null> => {
    try {
      return await withAbort<Processor[]>(async (signal) => {
        const cl = await useProdClient();
        return collectAllPages(
          (page, pageSize, sig) => cl.tests.fetchProcessors(instrumentId, testId, { page, pageSize }, sig),
          { signal },
        );
      });
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "fetchProcessors",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      return null;
    }
  };

  /**
   * Fetch one server page of processors plus the backend total (for
   * `v-data-table-server`). Propagates errors/aborts so the server-pagination
   * engine can classify them; returns null only when aborted. `pageSize <= 0`
   * (the "All" sentinel) walks every page via `collectAllPages` — `-1` is never
   * forwarded to the backend.
   */
  const fetchProcessorsPage = async (
    instrumentId: number,
    testId: number,
    params?: { page?: number; pageSize?: number },
  ): Promise<{ items: Processor[]; totalItems: number } | null> => {
    return withAbort<{ items: Processor[]; totalItems: number }>(async (signal) => {
      const cl = await useProdClient();
      if (params?.pageSize !== undefined && params.pageSize <= 0) {
        const items = await collectAllPages(
          (page, pageSize, sig) => cl.tests.fetchProcessors(instrumentId, testId, { page, pageSize }, sig),
          { signal },
        );
        return { items, totalItems: items.length };
      }
      const paged = unwrapPaged<Processor>(await cl.tests.fetchProcessors(instrumentId, testId, params, signal));
      return { items: paged.items, totalItems: paged.page.totalItems };
    });
  };

  /**
   * Create a new processor for a test.
   */
  const createProcessor = async (
    instrumentId: number,
    testId: number,
    data: ProcessorCreateRequest,
  ): Promise<Processor | null> => {
    try {
      return await withAbort<Processor>(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.createProcessor(instrumentId, testId, data, signal);
      });
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "createProcessor",
        fallbackKey: "components.common.errors.createFailed",
      });
      if (result.type === "abort") return null;
      return null;
    }
  };

  /**
   * Update an existing processor.
   */
  const updateProcessor = async (
    instrumentId: number,
    testId: number,
    processorId: number,
    data: ProcessorUpdateRequest,
  ): Promise<Processor | null> => {
    try {
      return await withAbort<Processor>(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.updateProcessor(instrumentId, testId, processorId, data, signal);
      });
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "updateProcessor",
        fallbackKey: "components.common.errors.updateFailed",
      });
      if (result.type === "abort") return null;
      return null;
    }
  };

  /**
   * Delete a processor.
   */
  const deleteProcessor = async (instrumentId: number, testId: number, processorId: number): Promise<boolean> => {
    try {
      await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.tests.deleteProcessor(instrumentId, testId, processorId, signal);
      });
      return true;
    } catch (err: unknown) {
      const result = handleError(err, {
        operation: "deleteProcessor",
        fallbackKey: "components.common.errors.deleteFailed",
      });
      if (result.type === "abort") return false;
      return false;
    }
  };

  return {
    // State
    loading: readonly(loading),
    error: readonly(error),
    tests: readonly(tests),
    testsByInstrument: readonly(testsByInstrument),

    // Computed
    testsCount: readonly(testsCount),
    enabledTests: readonly(enabledTests),
    disabledTests: readonly(disabledTests),
    testsGroupedByInstrument: readonly(testsGroupedByInstrument),

    // Fetch methods
    fetchTestsByInstrument,
    fetchTestsPage,
    fetchTestsFilter,
    fetchTestById,

    // CRUD
    createTest,
    updateTest,
    deleteTest,

    // Mappings (instrumentId required — all paths are nested)
    fetchMappings,
    fetchMappingsPage,
    createMapping,
    updateMapping,
    deleteMapping,

    // Processors (instrumentId required — all paths are nested)
    fetchProcessors,
    fetchProcessorsPage,
    createProcessor,
    updateProcessor,
    deleteProcessor,

    clearAll,
    abortAll,
  };
};
